/**
 * WorkflowConditionBuilder - Construtor visual de filtros do Workflow 2.0
 *
 * Gera o formato lido por `workflow_eval_conditions` no banco:
 *
 *   {
 *     "op": "and",                       // como os CONJUNTOS se combinam
 *     "groups": [                        // conjuntos de regras
 *       { "op": "and", "rules": [ { "field": "deal.value", "operator": "gt", "value": 1000 } ] },
 *       { "op": "or",  "rules": [ ... ] }
 *     ],
 *     "rules": [ ... ]                   // espelho do 1º conjunto (compatibilidade)
 *   }
 *
 * É usado em três lugares:
 *   - filtro do gatilho (bloco de Início);
 *   - regra do bloco de Condição (saídas Sim / Não);
 *   - condição da ligação (linha entre dois blocos).
 *
 * @version 2.0.0
 * RELEASE: 2026-09-01 v2.0.0 - Conjuntos de regras aninhados (E/OU), catálogo de
 *          campos por módulo e seletores tipados (sem digitação livre de UUID).
 * RELEASE: 2026-08-31 v1.0.0 - Criação inicial (Entrega 3 do plano Workflow 2.0)
 */

import { Layers, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { Ajuda } from './Ajuda';
import { SeletorDeCampo, ValorDaRegra } from './WorkflowConditionControls';
import { acharCampo, metaDoOperador, operadoresDoCampo } from './camposDoNegocio';
import { useWorkflowFilterSources } from '@/hooks/useWorkflowFilterSources';

// =============================================================================
// TIPOS E NORMALIZAÇÃO
// =============================================================================

/** Uma regra isolada da condição */
export interface RegraCondicao {
  field: string;
  operator: string;
  value?: unknown;
  /** Segundo valor (operador "está entre") */
  value2?: unknown;
}

/** Conjunto de regras com seu próprio conector E/OU */
export interface GrupoCondicao {
  op: 'and' | 'or';
  rules: RegraCondicao[];
}

/** Estrutura completa gravada no banco */
export interface CondicaoWorkflow {
  op: 'and' | 'or';
  groups: GrupoCondicao[];
  /** Espelho do primeiro conjunto — mantido por compatibilidade retroativa */
  rules?: RegraCondicao[];
}

/** Normaliza qualquer formato (array antigo, `{op,rules}` ou `{op,groups}`) */
export function normalizarCondicao(bruto: unknown): CondicaoWorkflow {
  // Formato mais antigo: array puro de regras
  if (Array.isArray(bruto)) {
    return { op: 'and', groups: [{ op: 'and', rules: bruto as RegraCondicao[] }] };
  }

  if (bruto && typeof bruto === 'object') {
    const obj = bruto as Record<string, unknown>;
    const op: 'and' | 'or' = obj.op === 'or' ? 'or' : 'and';

    // Formato novo com conjuntos
    if (Array.isArray(obj.groups)) {
      const groups = (obj.groups as Array<Record<string, unknown>>).map((g) => ({
        op: (g?.op === 'or' ? 'or' : 'and') as 'and' | 'or',
        rules: Array.isArray(g?.rules) ? (g.rules as RegraCondicao[]) : [],
      }));
      return { op, groups: groups.length > 0 ? groups : [{ op: 'and', rules: [] }] };
    }

    // Formato v1: um único conjunto em `rules`
    return {
      op: 'and',
      groups: [{ op, rules: Array.isArray(obj.rules) ? (obj.rules as RegraCondicao[]) : [] }],
    };
  }

  return { op: 'and', groups: [{ op: 'and', rules: [] }] };
}

/**
 * Serializa para gravação. Formato único aceito pelo motor (v3.0.0):
 * `{ op, groups: [{ op, rules: [...] }] }` — o formato antigo (`rules` na raiz)
 * foi descontinuado no banco.
 */
export function serializarCondicao(condicao: CondicaoWorkflow): CondicaoWorkflow {
  return {
    op: condicao.op,
    groups: condicao.groups.filter((g) => g.rules.length > 0),
  };
}

/** Quantidade total de regras configuradas */
export function contarRegras(condicao: unknown): number {
  return normalizarCondicao(condicao).groups.reduce((total, g) => total + g.rules.length, 0);
}

// =============================================================================
// COMPONENTE
// =============================================================================

interface Props {
  /** Condição atual (aceita formatos antigos) */
  valor: unknown;
  onChange: (condicao: CondicaoWorkflow) => void;
  /** Título exibido acima da lista de regras */
  titulo?: string;
  /** Texto do "?" ao lado do título */
  ajuda?: string;
  /** Oculta o cartão externo (usado dentro do modal, que já tem moldura) */
  semMoldura?: boolean;
  /** Entidade-raiz do processo (crm_deal | billing). Padrão: Negócio */
  entidade?: string | null;
}

export function WorkflowConditionBuilder({
  valor,
  onChange,
  titulo = 'Condições',
  ajuda,
  semMoldura = false,
  entidade,
}: Props) {
  const condicao = normalizarCondicao(valor);
  // v1.2.0: opções filtradas pelo módulo do processo (Tarefa/Retenção/Jurídico)
  const fontes = useWorkflowFilterSources(entidade);

  /** Aplica uma alteração em um conjunto específico */
  const atualizarGrupo = (indiceGrupo: number, patch: Partial<GrupoCondicao>) =>
    onChange({
      ...condicao,
      groups: condicao.groups.map((g, i) => (i === indiceGrupo ? { ...g, ...patch } : g)),
    });

  /** Aplica uma alteração em uma regra de um conjunto */
  const atualizarRegra = (indiceGrupo: number, indiceRegra: number, patch: Partial<RegraCondicao>) =>
    atualizarGrupo(indiceGrupo, {
      rules: condicao.groups[indiceGrupo].rules.map((r, i) =>
        i === indiceRegra ? { ...r, ...patch } : r,
      ),
    });

  const conteudo = (
    <div className="space-y-2">
      {(!semMoldura || titulo) && (
        <Label className="flex items-center gap-1.5 text-xs font-medium">
          {titulo}
          {ajuda && <Ajuda texto={ajuda} />}
        </Label>
      )}

      {condicao.groups.every((g) => g.rules.length === 0) && (
        <p className="text-xs text-muted-foreground">Sem condição — vale para todos os casos.</p>
      )}

      {condicao.groups.map((grupo, indiceGrupo) => (
        <div key={indiceGrupo}>
          {/* Conector ENTRE conjuntos (E / OU) */}
          {indiceGrupo > 0 && (
            <div className="flex items-center gap-2 py-1.5">
              <button
                type="button"
                onClick={() => onChange({ ...condicao, op: condicao.op === 'and' ? 'or' : 'and' })}
                className="rounded-full border bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary transition-colors hover:bg-primary/20"
                title="Clique para alternar entre E e OU entre os conjuntos"
              >
                {condicao.op === 'and' ? 'E' : 'OU'}
              </button>
              <span className="h-px flex-1 bg-border" />
            </div>
          )}

          <div className="space-y-2 rounded-xl border bg-muted/20 p-2">
            {/* Cabeçalho do conjunto */}
            {condicao.groups.length > 1 && (
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1 text-[11px] font-medium text-muted-foreground">
                  <Layers className="h-3 w-3" /> Conjunto {indiceGrupo + 1}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-destructive"
                  aria-label={`Remover conjunto ${indiceGrupo + 1}`}
                  onClick={() =>
                    onChange({ ...condicao, groups: condicao.groups.filter((_, i) => i !== indiceGrupo) })
                  }
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            )}

            {grupo.rules.map((regra, indiceRegra) => {
              const definicao = acharCampo(regra.field);
              const operadores = operadoresDoCampo(regra.field);
              const meta = metaDoOperador(regra.field, regra.operator);

              return (
                <div key={indiceRegra}>
                  {/* Conector DENTRO do conjunto */}
                  {indiceRegra > 0 && (
                    <div className="flex items-center gap-2 py-1">
                      <button
                        type="button"
                        onClick={() =>
                          atualizarGrupo(indiceGrupo, { op: grupo.op === 'and' ? 'or' : 'and' })
                        }
                        className="rounded-full border bg-muted px-2 py-0.5 text-[11px] font-medium text-foreground transition-colors hover:bg-accent"
                        title="Clique para alternar entre E e OU"
                      >
                        {grupo.op === 'and' ? 'E' : 'OU'}
                      </button>
                      <span className="h-px flex-1 bg-border" />
                    </div>
                  )}

                  {/* Linha: nº · campo · operador · valor · remover */}
                  <div className="flex flex-wrap items-center gap-1.5 rounded-lg border bg-card p-2">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] font-medium text-muted-foreground">
                      {indiceRegra + 1}
                    </span>

                    <SeletorDeCampo
                      valor={regra.field}
                      className="w-[150px]"
                      entidade={entidade}

                      onChange={(campo) => {
                        // Trocar o campo reinicia operador e valor (tipos diferentes)
                        const primeiro = operadoresDoCampo(campo.campo)[0];
                        atualizarRegra(indiceGrupo, indiceRegra, {
                          field: campo.campo,
                          operator: primeiro.valor,
                          value: primeiro.multiplo ? [] : '',
                          value2: undefined,
                        });
                      }}
                    />

                    <Select
                      value={regra.operator || operadores[0].valor}
                      onValueChange={(v) => {
                        const novo = metaDoOperador(regra.field, v);
                        atualizarRegra(indiceGrupo, indiceRegra, {
                          operator: v,
                          value: novo?.semValor
                            ? undefined
                            : novo?.multiplo
                              ? Array.isArray(regra.value)
                                ? regra.value
                                : regra.value
                                  ? [String(regra.value)]
                                  : []
                              : Array.isArray(regra.value)
                                ? (regra.value[0] ?? '')
                                : regra.value,
                        });
                      }}
                    >
                      <SelectTrigger className="h-8 w-[145px] rounded-lg text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {operadores.map((o) => (
                          <SelectItem key={o.valor} value={o.valor}>
                            {o.rotulo}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <ValorDaRegra
                      campo={regra.field}
                      operador={regra.operator || operadores[0].valor}
                      value={regra.value}
                      value2={regra.value2}
                      opcoes={definicao?.opcoes ?? fontes.opcoes(definicao?.fonte)}
                      onChange={(patch) => atualizarRegra(indiceGrupo, indiceRegra, patch)}
                    />

                    {meta?.semValor && <span className="flex-1" />}

                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive"
                      aria-label={`Remover condição ${indiceRegra + 1}`}
                      onClick={() =>
                        atualizarGrupo(indiceGrupo, {
                          rules: grupo.rules.filter((_, i) => i !== indiceRegra),
                        })
                      }
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              );
            })}

            <Button
              variant="outline"
              size="sm"
              className="h-8 rounded-lg"
              onClick={() =>
                atualizarGrupo(indiceGrupo, {
                  rules: [...grupo.rules, { field: 'deal.value', operator: 'gt', value: '' }],
                })
              }
            >
              <Plus className="mr-1 h-3.5 w-3.5" /> Condição
            </Button>
          </div>
        </div>
      ))}

      <Button
        variant="ghost"
        size="sm"
        className="h-8 rounded-lg text-xs text-muted-foreground"
        onClick={() =>
          onChange({ ...condicao, groups: [...condicao.groups, { op: 'and', rules: [] }] })
        }
      >
        <Layers className="mr-1 h-3.5 w-3.5" /> Adicionar conjunto de regras
      </Button>
    </div>
  );

  if (semMoldura) return conteudo;
  return <div className="rounded-xl border bg-card p-3 shadow-sm">{conteudo}</div>;
}
